import { Box, Grid, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import {
  FormDatePicker,
  FormTextField,
  GridCardContent,
} from "components/atoms";
import { AppTable, ReportHeader } from "components/molecules";
import { FormikHelpers, useFormik } from "formik";
import { DownloadHelper, TableHelper, TimeHelper } from "helpers";
import { usePaginationRequest } from "hooks/useRequest";
import { IGetSlowMovingStockRequest, ISlowMoving } from "model";
import moment from "moment";
import { useState } from "react";
import { ReportApiService } from "services";
import { I18nTranslator, translations } from "utils/i18n";
import { FIRST_PAGE } from "utils/Const";
import { useAppSelector } from "redux/configurations/reduxHooks";
import { LoadingButton } from "@mui/lab";
import { Download } from "@mui/icons-material";

const SlowMovingStockReport = () => {
  const [request, setRequest] = useState<IGetSlowMovingStockRequest>({
    productName: "",
    fromDate: TimeHelper.startOfWeek("MM-DD-YYYY"),
    toDate: TimeHelper.endOfWeek("MM-DD-YYYY"),
    pageNumber: FIRST_PAGE,
    pageSize: 100,
  });

  const handleSubmit = (values: IGetSlowMovingStockRequest) => {
    setRequest({
      ...request,
      ...values,
    });
  };

  const handleChangePage = (pageNumber: number) => {
    setRequest({
      ...request,
      pageNumber: pageNumber,
    });
  };
  const handlePageSizeChange = (pageSize: number) => {
    setRequest({ ...request, pageSize: pageSize });
  };

  return (
    <>
      <DetailHeader request={request} onSubmit={handleSubmit} />
      <TableSection
        request={request}
        onChangePage={handleChangePage}
        onPageSizeChange={handlePageSizeChange}
      />
    </>
  );
};

const DetailHeader = (props: {
  request: IGetSlowMovingStockRequest;
  onSubmit(values: IGetSlowMovingStockRequest): void;
}) => {
  const { request, onSubmit } = props;
  const currentWarehouse = useAppSelector(
    (state) => state.IdentityReducer.currentWarehouse
  );
  const [downloading, setDownloading] = useState(false);
  const formik = useFormik<any>({
    initialValues: {
      productName: "",
      fromDate: moment(request.fromDate, "MM-DD-YYYY"),
      toDate: moment(request.toDate, "MM-DD-YYYY"),
    },
    onSubmit: (values: any, { validateForm }: FormikHelpers<any>) => {
      const request: IGetSlowMovingStockRequest = {
        pageNumber: 1,
        productName: values.productName,
        fromDate: TimeHelper.formatMomentToDate(values.fromDate, "MM-DD-YYYY"),
        toDate: TimeHelper.formatMomentToDate(values.toDate, "MM-DD-YYYY"),
      };
      onSubmit(request);
    },
  });

  const _downloadSlowMoving = () => {
    const url = `Export/export-report-slow-moving?fromDate=${TimeHelper.formatDate(
      request?.fromDate! as string,
      "YYYY-MM-DD"
    )}&toDate=${TimeHelper.formatDate(
      request?.toDate! as string,
      "YYYY-MM-DD"
    )}&pageSize=1000&pageNumber=1&warehouseId=${currentWarehouse?.id}`;
    DownloadHelper.downloadFile(url, "audit-trail.xlsx", (loading) => {
      setDownloading(loading);
    });
  };
  return (
    <ReportHeader
      title={I18nTranslator(translations.routes.slowMovingStock)}
      formik={formik}
      loading={false}
      renderButtons={
        <Stack direction={"row"} spacing={2}>
          <LoadingButton
            startIcon={<Download />}
            loading={downloading}
            variant="contained"
            onClick={_downloadSlowMoving}
          >
            {I18nTranslator(translations.button.export)}
          </LoadingButton>
        </Stack>
      }
    >
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={3} item>
          <FormDatePicker
            id="fromDate"
            label={I18nTranslator(translations.fromDate)}
          />
        </Grid>
        <Grid xs={3} item>
          <FormDatePicker
            id="toDate"
            label={I18nTranslator(translations.toDate)}
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            id="productName"
            label={I18nTranslator(translations.report.productName)}
            placeholder={I18nTranslator(translations.placeholder.productName)}
          />
        </Grid>
      </Grid>
    </ReportHeader>
  );
};

const TableSection = (props: {
  request: IGetSlowMovingStockRequest;
  onChangePage(pageNumber: number): void;
  onPageSizeChange(pageSize: number): void;
}) => {
  const { request, onChangePage, onPageSizeChange } = props;
  const { loading, result } = usePaginationRequest<
    IGetSlowMovingStockRequest,
    ISlowMoving[]
  >(request, ReportApiService.getSlowMovingStock);

  const COLUMNS: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "palletCode",
      headerName: I18nTranslator(translations.report.palletCode),
      size: "small",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "locationCode",
      headerName: I18nTranslator(translations.report.locationCode),
      size: "small",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "locationPutDate",
      headerName: I18nTranslator(translations.report.locationPutDate),
      size: "small",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "productCode",
      headerName: I18nTranslator(translations.report.productCode),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "productName",
      headerName: I18nTranslator(translations.report.productName),
      size: "medium",
      align: "left",
    }),
    TableHelper.createCell({
      field: "productLot",
      headerName: I18nTranslator(translations.report.productLot),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "batchCodeDistributor",
      headerName: I18nTranslator(translations.report.batchCodeDistributor),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "uom",
      headerName: I18nTranslator(translations.report.uom),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "manufacturingDate",
      headerName: I18nTranslator(translations.report.manufacturingDate),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "expiryDate",
      headerName: I18nTranslator(translations.report.expiryDate),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "quantity",
      headerName: I18nTranslator(translations.report.inventoryQuantity),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "monthLeft",
      headerName: I18nTranslator(translations.report.timeLeft),
      size: "small",
      align: "right",
      renderHeader: () => {
        return (
          <Box sx={{}}>
            <Typography fontWeight={"600"}>
              {I18nTranslator(translations.report.timeLeft)}
            </Typography>
            <Typography fontWeight={"600"}>
              {I18nTranslator(translations.report.month)}
            </Typography>
          </Box>
        );
      },
      renderCell: ({ value }) => <Typography>{`-${value}`}</Typography>,
    }),
    TableHelper.createCell({
      field: "yearLeft",
      headerName: I18nTranslator(translations.report.timeLeft),
      size: "small",
      align: "right",
      renderHeader: () => {
        return (
          <Box sx={{}}>
            <Typography fontWeight={"600"}>
              {I18nTranslator(translations.report.timeLeft)}
            </Typography>
            <Typography fontWeight={"600"}>
              {I18nTranslator(translations.report.month)}
            </Typography>
          </Box>
        );
      },
      renderCell: ({ value }) => <Typography>{`-${value}`}</Typography>,
    }),
  ];

  return (
    <GridCardContent sx={{ flexDirection: "column" }}>
      <Box sx={{ width: "100%" }}>
        <AppTable<any>
          loading={loading}
          data={result}
          columns={COLUMNS}
          onSearch={(searchValue: string) => {}}
          searchPlaceholder={I18nTranslator(
            translations.placeholder.searchReceiveDocument
          )}
          onChangePage={onChangePage}
          hideHeader
          removeActionColumn
          defaultHeaderTable={170}
          onPageSizeChange={onPageSizeChange}
        />
      </Box>
    </GridCardContent>
  );
};

export default SlowMovingStockReport;
